import { HttpClient, HttpContext } from '@angular/common/http'
import { DestroyRef, inject, Injectable } from '@angular/core'
import { orderBy } from 'lodash'
import {
  map,
  Observable,
  shareReplay,
  Subject,
  switchMap,
  tap,
  catchError,
} from 'rxjs'
import {
  RLSCertMetadata,
  RLSCertMetadataResponse,
} from 'src/app/shared/models/rls-cert-metadata'
import { RLSCertUploadRequest } from 'src/app/shared/models/rls-cert-upload-request'
import { FilterParams, SortParams } from 'src/app/shared/utilities/http-params'
import { ToastService } from '../toasts/toasts.service'
import { PARSE_DATE_PROPS } from 'src/app/shared/interceptors/date-interceptor'
import { isAfter, isBefore, isSameDay, startOfDay } from 'date-fns'
import { utc } from 'src/app/shared/utilities/date'

const isDateField = (fName: string) => fName.includes('date')

const compareDates =
  (str1: string, str2: string) =>
    (fn: (d1: Date, d2: Date) => boolean) =>
      fn(startOfDay(utc(str1)), startOfDay(utc(str2)))

const filterLocallyByParams =
  (filters: readonly FilterParams[]) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <T extends Record<string, any>>(arr: T[]): T[] => {
      return filters.reduce(
        (acc, filter) =>
          acc.filter((item) =>
            filter.values.some((fv) => {
              if (fv.operation === '!=') {
                return isDateField(filter.name)
                  ? compareDates(
                    item[filter.name],
                    fv.value,
                  )((d1, d2) => isSameDay(d1, d2))
                  : item[filter.name] > fv.value
              }
              if (fv.operation === '<') {
                return isDateField(filter.name)
                  ? compareDates(
                    item[filter.name],
                    fv.value,
                  )((d1, d2) => isBefore(d1, d2))
                  : item[filter.name] < fv.value
              }
              if (fv.operation === '>') {
                return isDateField(filter.name)
                  ? compareDates(
                    item[filter.name],
                    fv.value,
                  )((d1, d2) => isAfter(d1, d2))
                  : item[filter.name] > fv.value
              }
              if (fv.operation === '<=') {
                return isDateField(filter.name)
                  ? compareDates(
                    item[filter.name],
                    fv.value,
                  )((d1, d2) => isBefore(d2, d2) || isSameDay(d1, d2))
                  : item[filter.name] <= fv.value
              }
              if (fv.operation === '>=') {
                return isDateField(filter.name)
                  ? compareDates(
                    item[filter.name],
                    fv.value,
                  )((d1, d2) => isAfter(d1, d2) || isSameDay(d1, d2))
                  : item[filter.name] >= fv.value
              }
              if (fv.operation === 'between=') {
                const [startDate, endDate] = fv.value.split(',').map(d => utc(d +'Z'))
                const isEndDateAfterValue =  isAfter(endDate, item[filter.name])
                const isStartDateBeforeValue =  isBefore(startDate, item[filter.name])
                return isEndDateAfterValue && isStartDateBeforeValue
              }
              if (fv.operation === '==') {
                return isDateField(filter.name)
                  ? compareDates(
                    item[filter.name],
                    fv.value,
                  )((d1, d2) => isSameDay(d1, d2))
                  : item[filter.name] === fv.value
              }
              if (fv.operation === 'in=') {
                return fv.value.split(',').some(str => str === item[filter.name])
              }
              return `${item[filter.name]}`
                .toLowerCase()
                .startsWith(fv.value.toLowerCase())
            }),
          ),
        arr,
      )
    }

const sortLocallyByOrder = (order: readonly SortParams[]) =>
// eslint-disable-next-line @typescript-eslint/no-explicit-any
  <T extends Record<string, any>>(arr: T[]): T[] =>
    orderBy<T>(
      arr,
      order.map((o) => o.name),
      order.map((o) => o.direction ?? 'asc'),
    )

@Injectable({
  providedIn: 'root',
})
export class RLSCertificatesService {
  private _http = inject(HttpClient)
  private _toast = inject(ToastService)
  private _destroyRef = inject(DestroyRef)

  private _rlsCertMetadataRefetchTrigger = new Subject<void>()
  private _rlsCertMetadataFetch = this._rlsCertMetadataRefetchTrigger.pipe(
    switchMap(() =>
      this._http.get<RLSCertMetadataResponse>('rls_certs/metadata', {
        context: new HttpContext().set(PARSE_DATE_PROPS, [
          'start_date',
          'expiration_date',
        ]),
      }),
    ),
    map((res) => res.results),
    shareReplay(1),
  )
  private _subscription = this._rlsCertMetadataFetch.subscribe()

  constructor() {
    this._destroyRef.onDestroy(() => {
      this._subscription.unsubscribe()
    })
  }

  refresh() {
    this._rlsCertMetadataRefetchTrigger.next(void 0)
  }

  getCertMetadata(
    filters: readonly FilterParams[] = [],
    order: readonly SortParams[] = [],
  ): Observable<RLSCertMetadata[]> {
    this._rlsCertMetadataRefetchTrigger.next(void 0)
    return this._rlsCertMetadataFetch.pipe(
      map(filterLocallyByParams(filters)),
      map(sortLocallyByOrder(order)),
    )
  }

  uploadCert(request: RLSCertUploadRequest) {
    const formData = new FormData()
    Object.entries(request).forEach(([k, v]) => formData.append(k, v))
    return this._http
      .post<string>('rls_certs', formData, {
        responseType: 'json',
        reportProgress: true,
      })
      .pipe(
        tap(() => {
          this._rlsCertMetadataRefetchTrigger.next(void 0)
        }),
        catchError((err) => {
          this._toast.show({
            type: 'error',
            message:
              'Something went wrong! Please review the provided information and try again.',
          })
          throw err
        }),
      )
  }

  downloadCert(participant: string) {
    return this._http
      .get<string>(`rls_certs/participant/${participant}/download`)
      .pipe(
        tap((url) => {
          const link = document.createElement('a')
          link.href = url
          link.click()
        }),
        catchError((err) => {
          this._toast.show({
            type: 'error',
            message: 'Something went wrong! Please try again.',
          })
          throw err
        }),
      )
  }

  deleteCert(participant: string) {
    return this._http.delete(`rls_certs/participant/${participant}`).pipe(
      tap(() => {
        this._rlsCertMetadataRefetchTrigger.next(void 0)
      }),
      catchError((err) => {
        this._toast.show({
          type: 'error',
          message: 'Something went wrong! Please try again.',
        })
        throw err
      }),
    )
  }
}
